import { useCallback, useEffect } from 'react';

// This matches the actual react-facebook-login response structure
type ReactFacebookLoginInfo = {
  accessToken?: string;
  data_access_expiration_time?: number;
  email?: string;
  expiresIn?: number;
  first_name?: string;
  graphDomain?: string;
  id?: string;
  last_name?: string;
  name?: string;
  picture?: {
    data?: {
      height?: number;
      is_silhouette?: boolean;
      url?: string;
      width?: number;
    };
  };
  signedRequest?: string;
  userID?: string;
  birthday?: string;
};

type ReactFacebookFailureResponse = {
  status?: string;
};

type FacebookLoginData = ReactFacebookLoginInfo | ReactFacebookFailureResponse;

type FacebookLoginResult = {
  firstname?: string;
  lastname?: string;
  birthday?: string;
  email?: string;
  socialMediaId: string;
  photo: string;
  type: 'facebook';
};

type UseFacebookLoginProps = {
  facebookId?: string;
  onLogin: (_data: FacebookLoginResult) => void;
  formState: any;
};

export const useFacebookLogin = ({ facebookId, onLogin, formState }: UseFacebookLoginProps) => {
  const handleFacebookLogin = useCallback(
    (response: FacebookLoginData) => {
      // Check if this is a successful login response
      if ('id' in response && response.id) {
        const { first_name: firstname, last_name: lastname, id: socialMediaId, email, picture, birthday } = response;

        const formFieldValues = {
          ...formState,
          firstname,
          lastname,
          birthday,
          email,
          socialMediaId,
          photo: picture?.data?.url || '',
          type: 'facebook' as const,
        };
        onLogin(formFieldValues);
      }
      // Handle failure case silently or log error
    },
    [formState, onLogin],
  );

  // Setup Facebook button accessibility
  useEffect(() => {
    if (!facebookId) return undefined;

    const facebookContainer = document?.getElementById('facebookButton');
    if (!facebookContainer) return undefined;

    const addAriaLabel = () => {
      const facebookButton = facebookContainer?.querySelector('span button');

      if (facebookButton && !facebookButton?.getAttribute('aria-label')) {
        facebookButton?.setAttribute('aria-label', 'Continue with Facebook');
        return true;
      }
      return false;
    };

    if (addAriaLabel()) return undefined;

    const observer = new MutationObserver(() => {
      if (addAriaLabel()) {
        observer.disconnect();
      }
    });

    observer.observe(facebookContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    const timeout = setTimeout(() => {
      observer.disconnect();
    }, 5000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [facebookId]);

  return {
    handleFacebookLogin,
  };
};
